import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Phone, MapPin, Menu, X, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/88797887-5fbe-4b8e-86d9-2a8b6e9a51f8/images/24255a550df67aeec175ac877a674e8bae385938fef42044bf92b3a8217b6427.png";
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/88797887-5fbe-4b8e-86d9-2a8b6e9a51f8/images/4c56b240c20871c59cedaec970f92f3dafdba8214d208fc99db99d2091e13279.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [media, setMedia] = useState([]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/media`);
        setMedia(data);
      } catch (err) {
        console.error('Failed to fetch media:', err);
      }
    };
    fetchMedia();
  }, []);

  const services = [
    {
      title: "Water Damage Restoration",
      description: "Expert restoration of flood and water-damaged properties with insurance claim support.",
      icon: "💧"
    },
    {
      title: "Basement Finishing",
      description: "Complete basement renovations including electrical, plumbing, and interior finishing.",
      icon: "🏠"
    },
    {
      title: "Insurance Projects",
      description: "Specialized expertise in insurance restoration projects with detailed Xactimate estimates.",
      icon: "📋"
    },
    {
      title: "Electrical & Plumbing",
      description: "Professional finishing work for all electrical outlets, fixtures, and plumbing installations.",
      icon: "⚡"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
              <img src={LOGO_URL} alt="Essex Street Contracting" className="h-10 sm:h-12 w-auto" />
              <div className="hidden sm:block">
                <p className="font-outfit font-bold text-[#0F172A] text-sm sm:text-base leading-tight">Essex Street</p>
                <p className="font-outfit font-medium text-[#0F172A] text-xs sm:text-sm leading-tight">Contracting LLC</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <a href="tel:347-938-3278" className="flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] transition-colors" data-testid="header-phone">
                <Phone className="h-4 w-4" />
                <span className="font-jakarta font-medium">347-938-3278</span>
              </a>
              
              {user ? (
                <div className="flex items-center gap-4">
                  <Link to="/dashboard">
                    <Button variant="outline" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-sm" data-testid="dashboard-btn">
                      Dashboard
                    </Button>
                  </Link>
                  <Button onClick={logout} variant="ghost" className="text-[#475569]" data-testid="logout-btn">
                    Logout
                  </Button>
                </div>
              ) : (
                <Link to="/login">
                  <Button className="bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] rounded-sm font-jakarta font-semibold" data-testid="client-login-btn">
                    Client Login
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#E2E8F0] py-4 px-4">
            <div className="flex flex-col gap-4">
              <a href="tel:347-938-3278" className="flex items-center gap-2 text-[#0F172A]" data-testid="mobile-phone">
                <Phone className="h-4 w-4" />
                <span className="font-jakarta">347-938-3278</span>
              </a>
              {user ? (
                <>
                  <Link to="/dashboard" className="font-jakarta font-medium text-[#0F172A]" data-testid="mobile-dashboard-link">Dashboard</Link>
                  <button onClick={logout} className="text-left font-jakarta text-[#475569]" data-testid="mobile-logout-btn">Logout</button>
                </>
              ) : (
                <Link to="/login" className="font-jakarta font-medium text-[#0F172A]" data-testid="mobile-login-link">Client Login</Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Modern basement interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="max-w-2xl">
            <p className="font-outfit text-[#D4AF37] text-xs sm:text-sm tracking-[0.2em] uppercase font-bold mb-4 animate-fade-in">
              NYC General Contractor
            </p>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in animate-delay-100">
              Expert Insurance Restoration & General Contracting
            </h1>
            <p className="font-jakarta text-base sm:text-lg text-gray-300 mb-8 leading-relaxed animate-fade-in animate-delay-200">
              Specialized in water damage restoration, basement finishing, and insurance claim projects. 
              Professional Xactimate estimates for seamless insurance submissions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animate-delay-300">
              <a href="tel:347-938-3278">
                <Button className="w-full sm:w-auto bg-[#D4AF37] text-[#0F172A] hover:bg-white font-jakarta font-semibold px-8 py-6 text-base rounded-sm" data-testid="hero-call-btn">
                  <Phone className="mr-2 h-5 w-5" />
                  Call for Free Estimate
                </Button>
              </a>
              <Link to="/estimate">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-[#0F172A] font-jakarta font-semibold px-8 py-6 text-base rounded-sm" data-testid="hero-estimate-btn">
                  View Sample Estimate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 sm:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-outfit text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-bold mb-4">Our Services</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#0F172A]">
              Professional Contracting Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="p-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm hover:border-[#D4AF37] transition-all duration-300 group"
                data-testid={`service-card-${index}`}
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="font-outfit text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-[#D4AF37] transition-colors">
                  {service.title}
                </h3>
                <p className="font-jakarta text-sm text-[#475569] leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 sm:py-24 bg-[#0F172A]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-outfit text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-bold mb-4">Why Choose Us</p>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
                Insurance Project Specialists
              </h2>
              <p className="font-jakarta text-gray-300 text-base leading-relaxed mb-8">
                We understand the complexities of insurance restoration projects. Our detailed Xactimate-formatted 
                estimates are designed to meet insurance adjuster standards, ensuring smooth claim processing 
                and faster approvals for your restoration project.
              </p>
              <ul className="space-y-4">
                {[
                  "Detailed Xactimate-formatted estimates",
                  "Direct communication with insurance adjusters",
                  "Licensed and insured in NYC",
                  "Quality workmanship guaranteed"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-200 font-jakarta">
                    <span className="text-[#D4AF37]">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1762146828422-50a8bd416d3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb25zdHJ1Y3Rpb24lMjBjb250cmFjdG9yJTIwYmx1ZXByaW50c3xlbnwwfHx8fDE3NzU1NTk5MzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional blueprints"
                className="w-full rounded-sm"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] p-6 rounded-sm">
                <p className="font-outfit text-3xl font-bold text-[#0F172A]">10+</p>
                <p className="font-jakarta text-sm text-[#0F172A]">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-20 sm:py-24 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-outfit text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-bold mb-4">Our Work</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-[#0F172A]">
              Project Gallery
            </h2>
            <p className="font-jakarta text-[#475569] mt-4 max-w-xl mx-auto">
              {media.length > 0 
                ? 'Browse photos and videos of our completed restoration projects.'
                : 'Photos and videos of our completed restoration projects coming soon.'}
            </p>
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.length > 0 ? (
              media.slice(0, 8).map((item) => (
                <div 
                  key={item.id}
                  className="aspect-square bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm overflow-hidden group relative"
                  data-testid={`gallery-item-${item.id}`}
                >
                  {item.media_type === 'video' ? (
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-full h-full flex items-center justify-center bg-[#0F172A]"
                    >
                      <div className="text-center">
                        <Play className="h-12 w-12 text-[#D4AF37] mx-auto mb-2" />
                        <p className="font-jakarta text-white text-sm">{item.title}</p>
                      </div>
                    </a>
                  ) : (
                    <>
                      <img 
                        src={item.url} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23F1F5F9" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%2394A3B8" font-size="12">Image</text></svg>';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                        <div>
                          <p className="font-jakarta text-white text-sm font-medium">{item.title}</p>
                          {item.category && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-[#D4AF37] text-[#0F172A] text-xs rounded-sm capitalize">
                              {item.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))
            ) : (
              // Placeholder grid when no media
              [1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                <div 
                  key={item}
                  className="aspect-square bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm flex items-center justify-center"
                  data-testid={`gallery-placeholder-${item}`}
                >
                  <span className="font-jakarta text-[#94A3B8] text-sm">Coming Soon</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 sm:py-24 bg-[#F8FAFC]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0F172A] rounded-sm p-8 sm:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
                  Ready to Start Your Project?
                </h2>
                <p className="font-jakarta text-gray-300 mb-8">
                  Contact us today for a free estimate. We're here to help with your insurance restoration 
                  and general contracting needs.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-white">
                    <Phone className="h-5 w-5 text-[#D4AF37]" />
                    <a href="tel:347-938-3278" className="font-jakarta hover:text-[#D4AF37] transition-colors">
                      347-938-3278
                    </a>
                  </div>
                  <div className="flex items-center gap-4 text-white">
                    <MapPin className="h-5 w-5 text-[#D4AF37]" />
                    <span className="font-jakarta">747 Essex Street, Brooklyn, NY 11208</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <a href="tel:347-938-3278">
                  <Button className="bg-[#D4AF37] text-[#0F172A] hover:bg-white font-jakarta font-semibold px-12 py-8 text-lg rounded-sm" data-testid="contact-call-btn">
                    <Phone className="mr-3 h-6 w-6" />
                    Call Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] border-t border-[#1E293B] py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Essex Street Contracting" className="h-10 w-auto" />
              <div>
                <p className="font-outfit font-bold text-white text-sm">Essex Street Contracting LLC</p>
                <p className="font-jakarta text-gray-400 text-xs">Brooklyn, NY</p>
              </div>
            </div>
            <p className="font-jakarta text-gray-400 text-sm">
              © {new Date().getFullYear()} Essex Street Contracting LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
